<template>
  <div v-if="isOpen" class="modal-backdrop-custom" @click.self="handleClose">
    <div class="modal-box" style="width: 900px; max-width: 95vw; max-height: 90vh; overflow-y: auto;">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 class="fw-bold mb-1">📄 청구서 상세</h6>
          <small class="text-muted">WaitingSeq: {{ target?.WaitingSeq }} | {{ target?.UserName }} ({{ target?.CarNumber }})</small>
        </div>
        <button class="btn-close" @click="handleClose"></button>
      </div>

      <div v-if="error" class="alert alert-danger py-2 mb-3">{{ error }}</div>

      <div v-if="payments.length === 0" class="text-center text-muted py-5">
        청구서가 없습니다.
      </div>
      <div v-else class="table-responsive mb-3">
        <table class="table table-sm table-hover align-middle">
          <thead class="table-light">
            <tr>
              <th style="width: 12%;">청구명</th>
              <th style="width: 7%;">청구번호</th>
              <th style="width: 7%;">원가</th>
              <th style="width: 7%;">결제액</th>
              <th style="width: 10%;">기간</th>
              <th style="width: 9%;">결제일자</th>
              <th style="width: 7%;">결제방식</th>
              <th style="width: 8%;">상태</th>
              <th style="width: 26%;">조치</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="pm in payments" :key="pm.PaymentSeq">
              <td class="fw-bold">{{ pm.BillName || '-' }}</td>
              <td class="text-muted small">{{ pm.PaymentSeq }}</td>
              <td>{{ pm.OriginalPrice?.toLocaleString() || 0 }}원</td>
              <td class="text-primary fw-bold">{{ pm.PaymentPrice?.toLocaleString() || 0 }}원</td>
              <td class="small text-muted">
                {{ pm.StartDate?.slice(0, 10) }} ~ {{ pm.EndDate?.slice(0, 10) }}
              </td>
              <td class="small text-muted">{{ pm.PaymentDate ? formatDatetime(pm.PaymentDate) : '-' }}</td>
              <td>
                <span v-if="pm.PaymentType === 'CARD'" class="badge bg-info">💳 카드</span>
                <span v-else-if="pm.PaymentType === 'CASH'" class="badge bg-warning">💵 현금</span>
                <span v-else class="badge bg-secondary">{{ pm.PaymentType || '-' }}</span>
              </td>
              <td>
                <span v-if="pm.Status === 'PAID'" class="badge bg-success">✓ 결제완료</span>
                <span v-else-if="pm.Status === 'READY'" class="badge bg-info">결제전</span>
                <span v-else class="badge bg-secondary">{{ pm.Status }}</span>
              </td>
              <td>
                <div class="d-flex gap-1 align-items-center flex-wrap">
                  <button 
                    v-if="pm.Tid"
                    class="btn btn-xs btn-info" 
                    @click="viewPaymentDetail(pm)"
                    style="padding: 0.25rem 0.5rem; font-size: 0.75rem;"
                    title="결제 상세 정보"
                  >
                    결제 상세
                  </button>
                  <button 
                    v-if="pm.Status !== 'PAID'" 
                    class="btn btn-xs btn-success" 
                    :disabled="processing"
                    @click="handleCompletePayment(pm)"
                    style="padding: 0.25rem 0.5rem; font-size: 0.75rem;"
                  >
                    {{ processing ? '처리중...' : '완료' }}
                  </button>
                  <button 
                    class="btn btn-xs btn-danger" 
                    :disabled="processing"
                    @click="handleDeletePayment(pm)"
                    style="padding: 0.25rem 0.5rem; font-size: 0.75rem;"
                  >청구서 삭제</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex gap-2 justify-content-end mt-3">
        <button class="btn btn-secondary btn-sm" @click="handleClose">닫기</button>
      </div>
    </div>
  </div>

  <!-- 결제 상세 정보 모달 -->
  <div v-if="paymentResultDetailModal" class="modal-backdrop-custom" @click.self="closePaymentResultDetail">
    <div class="modal-box" style="width: 800px; max-width: 95vw; max-height: 90vh; overflow-y: auto;">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 class="fw-bold mb-1">💳 결제 상세 정보</h6>
          <small class="text-muted" v-if="selectedPaymentResult">Tid: {{ selectedPaymentResult.Tid }}</small>
        </div>
        <button class="btn-close" @click="closePaymentResultDetail"></button>
      </div>

      <div v-if="paymentResultDetailError" class="alert alert-danger py-2 mb-3">{{ paymentResultDetailError }}</div>
      <div v-if="paymentResultDetailLoading" class="text-center text-muted py-5">로드 중...</div>

      <template v-else-if="selectedPaymentResult">
        <div class="row mb-3">
          <div class="col-md-6">
            <p><strong>회사명:</strong> {{ selectedPaymentResult.CoNm || '-' }}</p>
            <p><strong>Tid:</strong> <code style="font-size: 0.85rem;">{{ selectedPaymentResult.Tid || '-' }}</code></p>
            <p><strong>카드번호:</strong> {{ maskedCardNum(selectedPaymentResult.CardNum) }}</p>
            <p><strong>결제금액:</strong> <span class="text-danger fw-bold">{{ selectedPaymentResult.Amt ? selectedPaymentResult.Amt.toLocaleString() : '-' }} 원</span></p>
          </div>
          <div class="col-md-6">
            <p><strong>승인번호:</strong> <code>{{ selectedPaymentResult.AuthCode || '-' }}</code></p>
            <p><strong>인증일시:</strong> {{ selectedPaymentResult.AuthDate || '-' }}</p>
            <p><strong>결제수단:</strong> {{ selectedPaymentResult.PayMethod || '-' }}</p>
            <p><strong>카드사(발급):</strong> {{ selectedPaymentResult.AppCardName || '-' }}</p>
          </div>
        </div>
        <hr/>
        <div class="row">
          <div class="col-md-6">
            <p><strong>상품명:</strong> {{ selectedPaymentResult.GoodsName || '-' }}</p>
            <p><strong>결과코드:</strong> 
              <span :class="{ 'badge bg-success': selectedPaymentResult.ResultCode === '0000', 'badge bg-danger': selectedPaymentResult.ResultCode !== '0000' }">
                {{ selectedPaymentResult.ResultCode || '-' }}
              </span>
            </p>
            <p><strong>결과메시지:</strong> {{ selectedPaymentResult.ResultMsg || '-' }}</p>
          </div>
          <div class="col-md-6">
            <p><strong>카드사(매입):</strong> {{ selectedPaymentResult.AcquCardName || '-' }}</p>
            <p><strong>할부:</strong> {{ selectedPaymentResult.CardQuota || '일시불' }}</p>
            <p><strong>주차장:</strong> {{ selectedPaymentResult.AreaName || '-' }}</p>
            <p><strong>차량번호:</strong> {{ selectedPaymentResult.CarNum || '-' }}</p>
          </div>
        </div>
        <hr/>
        <div class="text-muted small">
          <p class="mb-1"><strong>구매자:</strong> {{ selectedPaymentResult.BuyerName || '-' }} | <strong>연락처:</strong> {{ selectedPaymentResult.BuyerTel || '-' }}</p>
          <p class="mb-1"><strong>이메일:</strong> {{ selectedPaymentResult.BuyerEmail || '-' }}</p>
          <p class="mb-0"><strong>등록일시:</strong> {{ formatDatetime(selectedPaymentResult.RegDate) }}</p>
        </div>
      </template>

      <div class="d-flex gap-2 justify-content-end mt-3">
        <button 
          class="btn btn-danger btn-sm" 
          @click="handleCancelPaymentResult"
          :disabled="paymentResultDetailLoading"
        >
          💳 카드 결제 취소
        </button>
        <button class="btn btn-secondary btn-sm" @click="closePaymentResultDetail">닫기</button>
      </div>
    </div>
  </div>

  <!-- 취소 결과 모달 -->
  <div v-if="cancelResultModal" class="modal-backdrop-custom" @click.self="closeCancelResultModal">
    <div class="modal-box" style="width: 600px; max-width: 95vw; max-height: 90vh; overflow-y: auto;">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="fw-bold mb-0">💳 결제 취소 결과</h6>
        <button class="btn-close" @click="closeCancelResultModal"></button>
      </div>

      <div v-if="cancelResult">
        <div class="row mb-3">
          <div class="col-md-6">
            <p><strong>회사명:</strong> {{ cancelResult.CoNm || '-' }}</p>
            <p><strong>Tid:</strong> <code>{{ cancelResult.Tid || '-' }}</code></p>
            <p><strong>결제 금액:</strong> {{ cancelResult.CancelAmt ? cancelResult.CancelAmt.toLocaleString() : '-' }} 원</p>
          </div>
          <div class="col-md-6">
            <p><strong>결과코드:</strong> 
              <span :class="{ 'badge bg-success': cancelResult.ResultCode === '0000', 'badge bg-danger': cancelResult.ResultCode !== '0000' }">
                {{ cancelResult.ResultCode || '-' }}
              </span>
            </p>
            <p><strong>결과메시지:</strong> {{ cancelResult.ResultMsg || '-' }}</p>
          </div>
        </div>
        <hr/>
        <div class="row">
          <div class="col-md-6">
            <p><strong>취소 금액:</strong> {{ cancelResult.CancelAmt ? cancelResult.CancelAmt.toLocaleString() : '-' }} 원</p>
            <p><strong>취소 번호:</strong> {{ cancelResult.CancelNum || '-' }}</p>
          </div>
          <div class="col-md-6">
            <p><strong>에러코드:</strong> {{ cancelResult.ErrorCode || '-' }}</p>
            <p><strong>에러메시지:</strong> {{ cancelResult.ErrorMsg || '-' }}</p>
          </div>
        </div>
      </div>

      <div class="d-flex gap-2 justify-content-end mt-3">
        <button class="btn btn-secondary btn-sm" @click="closeCancelResultModal">닫기</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { handleCancelPayment } from '../utils/payment'

export default {
  name: 'PaymentDetailModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    target: {
      type: Object,
      default: null
    },
    payments: {
      type: Array,
      default: () => []
    },
    processing: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      selectedPaymentResult: null,
      paymentResultDetailModal: false,
      paymentResultDetailLoading: false,
      paymentResultDetailError: null,
      cancelResult: null,
      cancelResultModal: false
    }
  },
  emits: ['close', 'complete-payment', 'delete-payment'],
  methods: {
    handleClose() {
      this.$emit('close')
    },
    handleCompletePayment(payment) {
      if (!confirm('이 결제를 완료 처리하겠습니까?')) return
      this.$emit('complete-payment', payment)
    },
    handleDeletePayment(payment) {
      if (!confirm('이 청구서를 삭제하면 복구할 수 없습니다. 정말 청구서 삭제를 진행하시겠습니까?')) return
      this.$emit('delete-payment', payment)
    },
    async viewPaymentDetail(payment) {
      if (!payment.Tid) {
        alert('Tid 정보가 없습니다.')
        return
      }
      
      this.selectedPaymentResult = null
      this.paymentResultDetailModal = true
      this.paymentResultDetailLoading = true
      this.paymentResultDetailError = null
      
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/payment-result/by-tid/${payment.Tid}`)
        if (response.data.success) {
          this.selectedPaymentResult = response.data.data
        } else {
          this.paymentResultDetailError = response.data.message || '결제 정보를 찾을 수 없습니다.'
        }
      } catch (error) {
        console.error('결제 상세 조회 실패:', error)
        this.paymentResultDetailError = error.response?.data?.message || '결제 정보 조회에 실패했습니다.'
      } finally {
        this.paymentResultDetailLoading = false
      }
    },
    closePaymentResultDetail() {
      this.paymentResultDetailModal = false
      this.selectedPaymentResult = null
      this.paymentResultDetailError = null
    },
    maskedCardNum(cardNum) {
      if (!cardNum) return '-'
      const num = cardNum.replace(/[^0-9]/g, '')
      if (num.length < 8) return num
      return num.substring(0, 4) + '-****-****-' + num.substring(12)
    },
    formatDatetime(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    async handleCancelPaymentResult() {
      if (!this.selectedPaymentResult?.Tid) {
        alert('Tid 정보가 없습니다.')
        return
      }

      // AreaSeq와 CarNum을 함께 전달
      const result = await handleCancelPayment({
        Tid: this.selectedPaymentResult.Tid,
        Amt: this.selectedPaymentResult.Amt,
        AreaSeq: this.selectedPaymentResult.AreaSeq || '', // 추가
        CarNum: this.selectedPaymentResult.CarNum || '',     // 추가
        GoodsName: this.selectedPaymentResult.GoodsName || ''
      })

      if (result) {
        this.paymentResultDetailModal = false
        this.cancelResult = result
        this.cancelResultModal = true
      }
    },
    closeCancelResultModal() {
      this.cancelResultModal = false
      this.cancelResult = null
    }
  }
}
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-box {
  background: #fff;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.table {
  margin-bottom: 0;
}
</style>
